import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import Header from "@/components/header";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2, Lock, Unlock } from "lucide-react";
import { useBitcoinBalance } from "@/hooks/use-bitcoin-balance";
import { useTranslation } from "@/i18n/LanguageContext";
import { useToast } from "@/hooks/use-toast";
import { useState, useEffect } from "react";
import { useTitle } from "@/hooks/use-title";
import { formatCurrency } from "@/lib/formatCurrency";

interface Hint {
  id: number;
  content?: string;
  price: number;
  puzzleId: number;
}

interface Puzzle {
  id: number;
  title: string;
  description: string;
  bitcoinAddress: string;
  hints: Hint[];
}

function PuzzleBalanceIndicator({ address }: { address: string }) {
  const { data: balance, isLoading } = useBitcoinBalance(address);
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-zinc-400">
        <Loader2 className="h-4 w-4 animate-spin" />
        {t("puzzles.checkingBalance")}
      </div>
    );
  }

  const balanceInBTC = balance ? balance / 100000000 : 0; // Convert satoshis to BTC

  if (balance === 0) {
    return <Badge variant="destructive">{t("puzzles.solved")}</Badge>;
  }

  return (
    <div className="flex flex-col items-end gap-1">
      <Badge variant="success">{t("puzzles.notSolved")}</Badge>
      <div className="text-sm text-zinc-400">
        {t("puzzles.balance")}: {balanceInBTC.toFixed(8)} BTC
      </div>
    </div>
  );
}

interface PurchaseResponse {
  success: boolean;
  hintId: number;
  newBalance: number;
  price: number;
  newPrice: number;
}

function PuzzleHints({ hints, puzzleId }: { hints: Hint[], puzzleId: number }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [purchasedHints, setPurchasedHints] = useState<number[]>([]);
  const { t, language } = useTranslation();

  const { data: user } = useQuery({
    queryKey: ["/api/auth/user"],
  });

  const { data: previouslyPurchasedHints } = useQuery<number[]>({
    queryKey: [`/api/puzzles/${puzzleId}/purchased-hints`],
    enabled: !!user,
  });

  useEffect(() => {
    if (previouslyPurchasedHints) {
      setPurchasedHints(previouslyPurchasedHints);
    }
  }, [previouslyPurchasedHints]);

  const purchaseHintMutation = useMutation<PurchaseResponse, Error, number>({
    mutationFn: async (hintId: number) => {
      const res = await fetch(`/api/puzzles/${puzzleId}/hints/${hintId}/purchase`, {
        method: 'POST',
        credentials: 'include',
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message || error.error);
      }

      return res.json();
    },
    onSuccess: (data, hintId) => {
      setPurchasedHints((prev) => [...prev, hintId]);
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
      queryClient.invalidateQueries({ queryKey: [`/api/puzzles/${puzzleId}`] });

      toast({ 
        title: t("puzzles.hintPurchaseSuccess"),
        description: t("puzzles.hintPurchaseSuccess")
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to purchase hint",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const { data: hintContents } = useQuery({
    queryKey: [`/api/puzzles/${puzzleId}/hints`, purchasedHints],
    queryFn: async () => {
      if (!purchasedHints.length) return {};

      const contents: Record<number, string> = {};
      for (const hintId of purchasedHints) {
        try {
          const res = await fetch(`/api/puzzles/${puzzleId}/hints/${hintId}/content`, {
            credentials: 'include'
          });
          if (res.ok) {
            const data = await res.json();
            contents[hintId] = data.content;
          }
        } catch (error) {
          console.error(`Error fetching hint ${hintId}:`, error);
        }
      }
      return contents;
    },
    enabled: purchasedHints.length > 0,
  });

  if (!hints?.length) return null;

  return (
    <div className="space-y-4 mt-6">
      <h3 className="text-lg font-semibold text-white">{t("puzzles.availableHints")}</h3>
      <div className="space-y-2">
        {hints.map((hint) => (
          <div key={hint.id} className="flex items-center gap-4 bg-zinc-800 p-3 rounded">
            {purchasedHints.includes(hint.id) ? (
              <>
                <Unlock className="h-4 w-4 text-green-500 shrink-0" />
                <p className="text-sm text-zinc-300">
                  {hintContents?.[hint.id] || t("puzzles.loadingHint")}
                </p>
              </>
            ) : (
              <>
                <Lock className="h-4 w-4 text-zinc-500 shrink-0" />
                <p className="text-sm text-zinc-500">{t("puzzles.hiddenHint")}</p>
                <div className="ml-auto flex items-center gap-2">
                  <span className="text-xs text-zinc-400">
                    {formatCurrency(hint.price, language)} cacatais
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => purchaseHintMutation.mutate(hint.id)}
                    disabled={purchaseHintMutation.isPending}
                  >
                    {t("puzzles.purchaseHint")}
                  </Button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function PuzzlePage({ params }: { params: { id: string } }) {
  const { data: puzzle, isLoading } = useQuery<Puzzle>({
    queryKey: [`/api/puzzles/${params.id}`],
  });
  const { t } = useTranslation();

  useTitle(puzzle?.title || "Bitcoin Challenge");

  if (isLoading || !puzzle) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <Header />
      <main className="container mx-auto px-6 pt-24 pb-12">
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-white">{puzzle.title}</CardTitle>
              <PuzzleBalanceIndicator address={puzzle.bitcoinAddress} />
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-zinc-300 mb-4">{puzzle.description}</p>
            <div className="space-y-2">
              <p className="text-sm text-zinc-400">{t("puzzles.bitcoinAddress")}:</p>
              <code className="block bg-zinc-950 p-2 rounded text-sm font-mono break-all text-zinc-300">
                {puzzle.bitcoinAddress}
              </code>
            </div>
            <PuzzleHints hints={puzzle.hints} puzzleId={puzzle.id} />
          </CardContent>
        </Card>
      </main>
    </div>
  );
}